// import styles
import "./PresenzeAdmin.css";

// import pacchetti
import { useState, useEffect, useCallback, useMemo } from "react";
import { timestamp } from "../../firebase/config";
import format from "date-fns/format";
import { it } from "date-fns/locale";
import {
  getMonth,
  getYear,
  isWithinInterval,
  eachDayOfInterval,
} from "date-fns";
import Select from "react-select";
import { downloadPresenzePdf } from "../../stampe/generatori/presenzePdf";
import { downloadPresenzeMultiPdf } from "../../stampe/generatori/presenzeMultiPdf";
import ValorizationModal from "../foglioPresenza/ValorizationModal";
import BonusManager from "./BonusManager";

// import hooks
import { useAuthContext } from "../../hooks/useAuthContext";
import { useIsAdmin } from "../../hooks/useIsAdmin";

// import componenti
import { database } from "../../firebase/config";

const isCessato = (u) => {
  const settore = String(u?.anagrafica?.settore || "")
    .trim()
    .toLowerCase();
  return settore === "cessati";
};

export default function PresenzeAdmin() {
  const { user } = useAuthContext();
  const admin = useIsAdmin(user);

  const fetchConfirmedBonusesForUser = useCallback(
    async (userId, year, month) => {
      if (!userId && userId !== 0) return null;
      const docId = `${userId}_${year}_${month}`;
      try {
        const doc = await database.collection("bonus_mensili").doc(docId).get();
        if (!doc.exists) return null;
        const data = doc.data();
        return Array.isArray(data?.bonuses) ? data.bonuses : [];
      } catch (_) {
        return null;
      }
    },
    [],
  );

  // State per la gestione della stampa PDF
  const [meseSelezionato, setMeseSelezionato] = useState(getMonth(new Date()));
  const [annoSelezionato, setAnnoSelezionato] = useState(getYear(new Date()));
  const [generatingMultiUserPdf, setGeneratingMultiUserPdf] = useState(false);
  const [mostraAnteprima, setMostraAnteprima] = useState(false);
  const [showValorizationPreview, setShowValorizationPreview] = useState(false);

  // State per la gestione degli utenti
  const [selectedUser, setSelectedUser] = useState(user.uid);
  const [users, setUsers] = useState([]);
  const [allUsersData, setAllUsersData] = useState([]);
  const [includeCessati, setIncludeCessati] = useState(false);

  const visibleUsers = useMemo(() => {
    return includeCessati ? users : users.filter((u) => !isCessato(u));
  }, [users, includeCessati]);

  // State per la gestione del periodo
  const [firstDayOfMonth, setFirstDayOfMonth] = useState(
    new Date(annoSelezionato, meseSelezionato, 1),
  );
  const [lastDayOfMonth, setLastDayOfMonth] = useState(
    new Date(annoSelezionato, meseSelezionato + 1, 0),
  );

  // State per tutte le presenze e reperibilità
  const [tuttePresenze, setTuttePresenze] = useState([]);
  const [tutteReperibilita, setTutteReperibilita] = useState([]);
  const [tutteSpese, setTutteSpese] = useState([]);
  const [tuttiDocumenti, setTuttiDocumenti] = useState([]);
  const [presenzeUtente, setPresenzeUtente] = useState([]);
  const [reperibilitaUtente, setReperibilitaUtente] = useState([]);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allConfirmedBonuses, setAllConfirmedBonuses] = useState({});

  // Recupera i bonus confermati per tutti gli utenti quando cambia mese/anno
  useEffect(() => {
    const fetchConfirmedBonuses = async () => {
      if (!visibleUsers.length) return;

      const bonuses = {};
      const promises = visibleUsers.map(async (u) => {
        const docId = `${u.id}_${annoSelezionato}_${meseSelezionato}`;
        try {
          const doc = await database
            .collection("bonus_mensili")
            .doc(docId)
            .get();
          if (doc.exists) {
            bonuses[u.id] = doc.data().bonuses || [];
          } else {
            bonuses[u.id] = null;
          }
        } catch (error) {
          console.error("Errore recupero bonus:", error);
          bonuses[u.id] = null;
        }
      });

      await Promise.all(promises);
      setAllConfirmedBonuses(bonuses);
    };

    fetchConfirmedBonuses();
  }, [visibleUsers, annoSelezionato, meseSelezionato]);

  // State per la gestione massiva presenze
  const [selectedUsersForFesta, setSelectedUsersForFesta] = useState([]);
  const [festaDate, setFestaDate] = useState("");
  const [festaNota, setFestaNota] = useState("Festa");
  const [isSubmittingFesta, setIsSubmittingFesta] = useState(false);

  // State per inserimento periodico (range di date)
  const [periodicStartDate, setPeriodicStartDate] = useState("");
  const [periodicEndDate, setPeriodicEndDate] = useState("");
  const [periodicType, setPeriodicType] = useState("lavorativa");
  const [periodicIngresso, setPeriodicIngresso] = useState("08:00");
  const [periodicUscita, setPeriodicUscita] = useState("17:00");
  const [periodicCantiere, setPeriodicCantiere] = useState(null);
  const [periodicMezzo, setPeriodicMezzo] = useState(null);
  const [periodicPausa, setPeriodicPausa] = useState("1");
  const [periodicNote, setPeriodicNote] = useState("");
  const [periodicCodiceMalattia, setPeriodicCodiceMalattia] = useState("");
  const [isSubmittingPeriodic, setIsSubmittingPeriodic] = useState(false);

  // State per migrazione codici cantiere
  const [migrationPreview, setMigrationPreview] = useState(null);
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [automezzi, setAutomezzi] = useState([]);

  // Aggiorna il periodo quando cambiano mese o anno
  useEffect(() => {
    console.log(
      "Aggiornamento periodo - Mese:",
      meseSelezionato,
      "Anno:",
      annoSelezionato,
    );
    setFirstDayOfMonth(new Date(annoSelezionato, meseSelezionato, 1));
    setLastDayOfMonth(new Date(annoSelezionato, meseSelezionato + 1, 0));
  }, [meseSelezionato, annoSelezionato]);

  // Recupera tutti gli utenti
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersSnapshot = await database.collection("users").get();
        const usersData = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUsers(usersData);
      } catch (error) {
        console.error("Errore nel recupero degli utenti:", error);
      }
    };

    if (admin) {
      fetchUsers();
    }
  }, [admin]);

  // Recupera tutte le presenze
  const fetchAllData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [
        presenzeSnapshot,
        reperibilitaSnapshot,
        projectsSnapshot,
        documentiSnapshot,
        automezziSnapshot,
        speseSnapshot,
      ] = await Promise.all([
        database
          .collection("presenze")
          .where("dataRegistrazione", ">=", timestamp.fromDate(firstDayOfMonth))
          .where("dataRegistrazione", "<=", timestamp.fromDate(lastDayOfMonth))
          .orderBy("dataRegistrazione", "asc")
          .get(),
        database
          .collection("reperibilita")
          .where("dataInizio", ">=", timestamp.fromDate(firstDayOfMonth))
          .where("dataInizio", "<=", timestamp.fromDate(lastDayOfMonth))
          .orderBy("dataInizio", "asc")
          .get(),
        database.collection("projects").get(),
        database.collection("documenti").get(),
        database.collection("automezzi").get(),
        database.collection("spese").get(),
      ]);

      const presenzeData = presenzeSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const reperibilitaData = reperibilitaSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const projectsData = projectsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const documentiData = documentiSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const automezziData = automezziSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const speseData = speseSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setTuttePresenze(presenzeData);
      setTutteReperibilita(reperibilitaData);
      setProjects(projectsData);
      setTuttiDocumenti(documentiData);
      setAutomezzi(automezziData);
      setTutteSpese(speseData);
      setIsLoading(false);
    } catch (error) {
      console.error("Errore nel recupero dei dati:", error);
      setError("Impossibile recuperare i dati dal database");
      setIsLoading(false);
    }
  }, [firstDayOfMonth, lastDayOfMonth]);

  // Recupera tutte le presenze una sola volta all'avvio
  useEffect(() => {
    if (admin) {
      fetchAllData();
    }
  }, [admin, fetchAllData]);

  // Filtra le presenze in base all'utente selezionato e al periodo
  useEffect(() => {
    if (!tuttePresenze.length) return;

    console.log("Filtraggio presenze per:", {
      userId: selectedUser,
      firstDay: firstDayOfMonth,
      lastDay: lastDayOfMonth,
    });

    const presenzeFiltrate = tuttePresenze.filter((presenza) => {
      // Verifica che l'utente corrisponda
      if (presenza.userId !== selectedUser) {
        return false;
      }

      // Verifica che la data sia nel periodo selezionato
      const dataPresenza = presenza.dataRegistrazione.toDate();
      return isWithinInterval(dataPresenza, {
        start: firstDayOfMonth,
        end: lastDayOfMonth,
      });
    });

    // Ordina le presenze per data
    const presenzeOrdinate = [...presenzeFiltrate].sort((a, b) => {
      return a.dataRegistrazione.toDate() - b.dataRegistrazione.toDate();
    });

    // Filtra reperibilità
    const reperibilitaFiltrate = tutteReperibilita
      .filter((rep) => {
        if (rep.userId !== selectedUser) return false;

        // Verifica sovrapposizione periodo
        const start = rep.dataInizio?.toDate();
        const end = rep.dataFine?.toDate();

        if (!start || !end) return false;

        // Logica sovrapposizione: (StartA <= EndB) and (EndA >= StartB)
        return start <= lastDayOfMonth && end >= firstDayOfMonth;
      })
      .sort(
        (a, b) => (a.dataInizio?.toDate() || 0) - (b.dataInizio?.toDate() || 0),
      );

    setReperibilitaUtente(reperibilitaFiltrate);

    setPresenzeUtente(presenzeOrdinate);
    console.log("Dati filtrati per utente:", {
      presenze: presenzeOrdinate.length,
      reperibilita: reperibilitaFiltrate.length,
    });
  }, [
    tuttePresenze,
    tutteReperibilita,
    selectedUser,
    firstDayOfMonth,
    lastDayOfMonth,
  ]);

  // Rimosso re-render forzato del componente PDF: non necessario con jsPDF

  // Prepara i dati per il PDF multi-utente quando cambiano le presenze o i filtri
  useEffect(() => {
    if (tuttePresenze.length > 0 && visibleUsers.length > 0) {
      const usersData = visibleUsers.map((u) => {
        // Filtra le presenze per questo utente nel periodo selezionato
        const presenzeUtente = tuttePresenze.filter((presenza) => {
          const dataPresenza = presenza.dataRegistrazione.toDate();
          return (
            presenza.userId === u.id &&
            isWithinInterval(dataPresenza, {
              start: firstDayOfMonth,
              end: lastDayOfMonth,
            })
          );
        });

        // Ordina le presenze per data
        const presenzeSorted = [...presenzeUtente].sort((a, b) => {
          return a.dataRegistrazione.toDate() - b.dataRegistrazione.toDate();
        });

        // Filtra reperibilità per utente
        const reperibilitaUtente = tutteReperibilita
          .filter((rep) => {
            if (rep.userId !== u.id) return false;
            const start = rep.dataInizio?.toDate();
            const end = rep.dataFine?.toDate();
            if (!start || !end) return false;
            return start <= lastDayOfMonth && end >= firstDayOfMonth;
          })
          .sort(
            (a, b) =>
              (a.dataInizio?.toDate() || 0) - (b.dataInizio?.toDate() || 0),
          );

        // Assicurati che l'oggetto user abbia sempre anagrafica definito
        const userWithSafeAnagrafica = {
          ...u,
          anagrafica: u.anagrafica || null,
        };

        return {
          user: userWithSafeAnagrafica,
          presenze: presenzeSorted,
          reperibilita: reperibilitaUtente,
          confirmedBonuses: allConfirmedBonuses[u.id] || null,
        };
      });

      setAllUsersData(usersData);
    }
  }, [
    tuttePresenze,
    tutteReperibilita,
    visibleUsers,
    firstDayOfMonth,
    lastDayOfMonth,
    allConfirmedBonuses,
  ]);

  useEffect(() => {
    if (!users.length) return;
    if (includeCessati) return;
    const selected = users.find((u) => u.id === selectedUser);
    if (selected && isCessato(selected)) {
      const first = users.find((u) => !isCessato(u));
      if (first) setSelectedUser(first.id);
    }
  }, [includeCessati, users, selectedUser]);

  useEffect(() => {
    if (includeCessati) return;
    const visibleIds = new Set(visibleUsers.map((u) => u.id));
    setSelectedUsersForFesta((prev) =>
      prev.filter((opt) => visibleIds.has(opt.value)),
    );
  }, [includeCessati, visibleUsers]);

  // Stili personalizzati per react-select
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "40px !important",
      border: "1px solid #ddd !important",
      boxShadow: state.isFocused
        ? "0 0 0 1px #007bff !important"
        : "none !important",
      "&:hover": {
        borderColor: "#007bff !important",
      },
      width: "100% !important",
      borderColor: state.isFocused ? "#007bff !important" : "#ddd !important",
      padding: "0 !important",
      margin: "0 !important",
      backgroundColor: "#fff !important",
      color: "#333 !important",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 10000,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 10000,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#999 !important",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#007bff !important"
        : state.isFocused
          ? "#f8f9fa !important"
          : "#fff !important",
      color: state.isSelected ? "#fff !important" : "#333 !important",
      "&:hover": {
        backgroundColor: "#f8f9fa !important",
        color: "#333 !important",
      },
    }),
  };

  // Gestisce il cambio del mese per la stampa PDF
  const handleChangeMese = (selectedOption) => {
    const mese = selectedOption.value;
    console.log("Cambio mese - Nuovo valore:", mese);
    setMeseSelezionato(mese);
    setMostraAnteprima(true);
    console.log(
      "Dopo setMeseSelezionato - Nuove date saranno calcolate nell'useEffect",
    );
    // Il re-render del componente PDF viene gestito dall'useEffect
  };

  // Gestisce il cambio dell'anno per la stampa PDF
  const handleChangeAnno = (selectedOption) => {
    const anno = selectedOption.value;
    console.log("Cambio anno - Nuovo valore:", anno);
    setAnnoSelezionato(anno);
    setMostraAnteprima(true);
    console.log(
      "Dopo setAnnoSelezionato - Nuove date saranno calcolate nell'useEffect",
    );
    // Il re-render del componente PDF viene gestito dall'useEffect
  };

  // Gestisce il cambio dell'utente selezionato
  const handleChangeUser = (selectedOption) => {
    console.log(
      "Cambio utente selezionato - Valore selezionato:",
      selectedOption.value,
    );
    console.log("Utente precedente:", selectedUser);
    setSelectedUser(selectedOption.value);
    setMostraAnteprima(true);
    // Il re-render del componente PDF viene gestito dall'useEffect
  };

  // Funzione per ordinare le presenze per il PDF e l'anteprima
  const ordinaPresenze = (presenze) => {
    if (!presenze || presenze.length === 0) return [];

    // Filtra le presenze che hanno dataRegistrazione valida
    const presenzeValide = presenze.filter((p) => p && p.dataRegistrazione);

    // Ordina le presenze per data
    return [...presenzeValide].sort((a, b) => {
      return a.dataRegistrazione.toDate() - b.dataRegistrazione.toDate();
    });
  };

  // Log delle presenze per debug
  useEffect(() => {
    if (presenzeUtente && presenzeUtente.length > 0) {
      console.log(
        "Dati presenze per utente:",
        selectedUser,
        "Numero presenze:",
        presenzeUtente.length,
      );
    }
  }, [presenzeUtente, selectedUser]);

  // Verifica se l'utente è un amministratore
  if (!admin) {
    return (
      <div className="presenze-container">
        <h2>Accesso non autorizzato</h2>
        <p>Solo gli amministratori possono accedere a questa pagina.</p>
      </div>
    );
  }

  // Opzioni per i select
  const userOptions = visibleUsers.map((u) => ({
    value: u.id,
    label: u.anagrafica
      ? `${u.anagrafica.nome} ${u.anagrafica.cognome}`
      : u.displayName || u.email,
  }));

  const monthOptions = [
    { value: 0, label: "Gennaio" },
    { value: 1, label: "Febbraio" },
    { value: 2, label: "Marzo" },
    { value: 3, label: "Aprile" },
    { value: 4, label: "Maggio" },
    { value: 5, label: "Giugno" },
    { value: 6, label: "Luglio" },
    { value: 7, label: "Agosto" },
    { value: 8, label: "Settembre" },
    { value: 9, label: "Ottobre" },
    { value: 10, label: "Novembre" },
    { value: 11, label: "Dicembre" },
  ];

  const yearOptions = [
    { value: 2022, label: "2022" },
    { value: 2023, label: "2023" },
    { value: 2024, label: "2024" },
    { value: 2025, label: "2025" },
    { value: 2026, label: "2026" },
    { value: 2027, label: "2027" },
  ];

  // Funzione per l'inserimento massivo di "Festa"
  const handleBulkFestaCreation = async () => {
    if (!festaDate) {
      alert("Seleziona una data per la festività.");
      return;
    }
    if (selectedUsersForFesta.length === 0) {
      alert("Seleziona almeno un dipendente.");
      return;
    }

    const confirmMessage = `Stai per inserire la presenza "${festaNota}" per ${selectedUsersForFesta.length} dipendenti in data ${festaDate}. Confermi?`;
    if (!window.confirm(confirmMessage)) return;

    setIsSubmittingFesta(true);

    try {
      const batchPromises = selectedUsersForFesta.map(async (userOption) => {
        const userId = userOption.value;
        const userObj = users.find((u) => u.id === userId);
        // Assicura che userEmail non sia mai undefined
        const userEmail = userObj?.email || "";

        const presenzaData = {
          userId: userId,
          userEmail: userEmail,
          dataRegistrazione: new Date(festaDate + "T00:00:00"),
          tipoPresenza: "festa",
          oraIngresso: null,
          oraUscita: null,
          cantiere: null,
          mezzo: null,
          durataPausa: null,
          isFerie: false,
          isMalattia: false,
          isRiposo: true,
          codiceMalattia: null,
          note: festaNota,
          hasTimbratura: false,
          createdAt: new Date(),
        };

        return database.collection("presenze").add(presenzaData);
      });

      await Promise.all(batchPromises);

      alert("Inserimento massivo completato con successo!");

      // Reset form
      setSelectedUsersForFesta([]);
      setFestaDate("");
      setFestaNota("Festa");

      // Ricarica i dati
      fetchAllData();
    } catch (error) {
      console.error("Errore nell'inserimento massivo:", error);
      alert("Si è verificato un errore durante l'inserimento.");
    } finally {
      setIsSubmittingFesta(false);
    }
  };

  // Funzione per l'inserimento periodico (range di date)
  const handleBulkPeriodicCreation = async () => {
    if (!selectedUser) {
      alert("Seleziona un dipendente.");
      return;
    }
    if (!periodicStartDate || !periodicEndDate) {
      alert("Seleziona sia la data di inizio che la data di fine.");
      return;
    }
    if (new Date(periodicStartDate) > new Date(periodicEndDate)) {
      alert("La data di fine non può essere precedente alla data di inizio.");
      return;
    }

    // Calcola i giorni nell'intervallo
    const start = new Date(periodicStartDate);
    const end = new Date(periodicEndDate);
    const days = eachDayOfInterval({ start, end });

    const confirmMessage = `Stai per inserire ${days.length} presenze di tipo "${periodicType}" per l'utente selezionato. Confermi?`;
    if (!window.confirm(confirmMessage)) return;

    setIsSubmittingPeriodic(true);

    try {
      const userObj = users.find((u) => u.id === selectedUser);
      const userEmail = userObj?.email || "";

      const batchPromises = days.map(async (day) => {
        // Costruisci l'oggetto presenza base
        const presenzaData = {
          userId: selectedUser,
          userEmail: userEmail,
          dataRegistrazione: new Date(day), // Imposta la data corretta (00:00:00 local time usually)
          tipoPresenza: periodicType,
          createdAt: new Date(),
          // Default nulls
          oraIngresso: null,
          oraUscita: null,
          cantiere: null,
          mezzo: null,
          durataPausa: null,
          isFerie: false,
          isMalattia: false,
          isRiposo: false,
          codiceMalattia: null,
          note: periodicNote,
          hasTimbratura: false,
        };

        // Personalizza in base al tipo
        if (periodicType === "lavorativa") {
          // Imposta orari
          if (periodicIngresso) {
            const [hours, minutes] = periodicIngresso.split(":");
            const ingressoDate = new Date(day);
            ingressoDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
            presenzaData.oraIngresso = ingressoDate;
          }
          if (periodicUscita) {
            const [hours, minutes] = periodicUscita.split(":");
            const uscitaDate = new Date(day);
            uscitaDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
            presenzaData.oraUscita = uscitaDate;
          }
          // Imposta cantiere
          if (periodicCantiere) {
            presenzaData.cantiere = periodicCantiere; // Oggetto {nome, codice, ...}
          }
          // Imposta mezzo e pausa
          if (periodicMezzo) {
            presenzaData.mezzo = periodicMezzo; // Oggetto mezzo completo
          }
          presenzaData.durataPausa = periodicPausa || "1"; // Default 1 ora
        } else if (periodicType === "ferie") {
          presenzaData.isFerie = true;
        } else if (periodicType === "malattia") {
          presenzaData.isMalattia = true;
          presenzaData.codiceMalattia = periodicCodiceMalattia;
        } else if (periodicType === "riposo") {
          presenzaData.isRiposo = true;
        } else if (periodicType === "permesso") {
          // Permesso is usually treated as generic or short leave,
          // but structure expects boolean flags. Let's use note for now or treat as generic.
          // If the system supports "permesso", good. Otherwise maybe just note.
          // Looking at FoglioPresenza, types are: lavorativa, ferie, riposo. Malattia via separate flag.
          // So "permesso" might need to be just a note or specific logic.
          // For now, let's stick to known types: lavorativa, ferie, malattia, riposo.
        }

        return database.collection("presenze").add(presenzaData);
      });

      await Promise.all(batchPromises);

      alert("Inserimento periodico completato con successo!");

      // Reset partial
      setPeriodicNote("");
      // Non resettiamo le date per permettere inserimenti consecutivi o simili se serve?
      // Meglio resettare per sicurezza
      // setPeriodicStartDate("");
      // setPeriodicEndDate("");

      fetchAllData();
    } catch (error) {
      console.error("Errore nell'inserimento periodico:", error);
      alert("Si è verificato un errore durante l'inserimento.");
    } finally {
      setIsSubmittingPeriodic(false);
    }
  };

  // Funzione per esportare in CSV
  const handleExportCsv = () => {
    if (!allUsersData || allUsersData.length === 0) {
      alert("Nessun dato da esportare.");
      return;
    }

    const hasData = allUsersData.some((u) => u.presenze.length > 0);
    if (!hasData) {
      alert("Nessuna presenza trovata nel periodo selezionato.");
      return;
    }

    // Intestazione CSV
    const headers = [
      "Dipendente",
      "Data",
      "Tipo Presenza",
      "Ingresso",
      "Uscita",
      "Codice Cantiere",
      "Cantiere",
      "Note",
      "Ore (Stimate)",
    ];

    const rows = [];

    allUsersData.forEach((userData) => {
      // Skip users with no presence
      if (userData.presenze.length === 0) return;

      const nomeDipendente = userData.user.anagrafica
        ? `${userData.user.anagrafica.nome} ${userData.user.anagrafica.cognome}`
        : userData.user.displayName || userData.user.email;

      userData.presenze.forEach((presenza) => {
        const data = format(presenza.dataRegistrazione.toDate(), "dd/MM/yyyy");
        const tipo = presenza.tipoPresenza;

        let ingresso = "";
        let uscita = "";

        if (
          presenza.oraIngresso &&
          typeof presenza.oraIngresso.toDate === "function"
        ) {
          ingresso = format(presenza.oraIngresso.toDate(), "HH:mm");
        }

        if (
          presenza.oraUscita &&
          typeof presenza.oraUscita.toDate === "function"
        ) {
          uscita = format(presenza.oraUscita.toDate(), "HH:mm");
        }

        const codiceCantiere = presenza.cantiere?.codice || "";
        const cantiere = presenza.cantiere?.nome || "";
        // Clean notes from newlines for CSV safety and escape quotes
        const note = (presenza.note || "")
          .replace(/(\r\n|\n|\r)/gm, " ")
          .replace(/"/g, '""');

        // Simple hour calc for reference
        let ore = "";
        if (presenza.oraIngresso && presenza.oraUscita) {
          const diffMs =
            presenza.oraUscita.toDate() - presenza.oraIngresso.toDate();
          const diffHrs = (diffMs / (1000 * 60 * 60)).toFixed(2);
          ore = diffHrs.replace(".", ","); // Excel prefers comma for decimals in IT locale usually
        }

        // Handle quoted fields
        rows.push(
          [
            `"${nomeDipendente}"`,
            data,
            tipo,
            ingresso,
            uscita,
            `"${codiceCantiere}"`,
            `"${cantiere}"`,
            `"${note}"`,
            ore,
          ].join(","),
        );
      });
    });

    const csvContent = [headers.join(","), ...rows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    const label = format(
      new Date(annoSelezionato, meseSelezionato),
      "MMMM_yyyy",
      { locale: it },
    );
    link.setAttribute("download", `presenze_export_${label}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Funzione per analizzare e preparare la migrazione dei codici cantiere
  const analyzePresenzeForMigration = () => {
    console.log("Inizio analisi migrazione...");
    const updates = [];
    let processed = 0;

    tuttePresenze.forEach((presenza) => {
      // Ignora ferie, malattia, riposo
      if (presenza.isFerie || presenza.isMalattia || presenza.isRiposo) return;

      const cantiere = presenza.cantiere;
      let cantiereNome = null;

      // Determina il nome del cantiere
      if (typeof cantiere === "string") {
        cantiereNome = cantiere;
      } else if (cantiere && typeof cantiere === "object") {
        cantiereNome = cantiere.nome || cantiere.label;
      }

      if (!cantiereNome) return;

      // Cerca il progetto corrispondente
      const project = projects.find((p) => p.nome === cantiereNome);

      if (project && project.codice) {
        // Verifica se è necessario l'aggiornamento
        // Aggiorna se:
        // 1. cantiere è una stringa (deve diventare oggetto)
        // 2. cantiere è oggetto ma manca il codice
        // 3. cantiere è oggetto ma il codice è diverso (o errato)

        const needsUpdate =
          typeof cantiere === "string" ||
          !cantiere.codice ||
          cantiere.codice !== project.codice;

        if (needsUpdate) {
          updates.push({
            presenzaId: presenza.id,
            oldCantiere: cantiere,
            newCantiere: {
              nome: project.nome,
              codice: project.codice,
              // Mantieni altre proprietà se presenti e se cantiere era oggetto
              ...(typeof cantiere === "object" ? cantiere : {}),
            },
            dataRegistrazione: presenza.dataRegistrazione,
            user:
              users.find((u) => u.id === presenza.userId)?.displayName ||
              "Utente sconosciuto",
          });
        }
      }
      processed++;
    });

    console.log(
      `Analisi completata. ${updates.length} presenze da aggiornare su ${processed} processate.`,
    );
    setMigrationPreview(updates);
    setShowMigrationModal(true);
  };

  // Funzione per eseguire la migrazione
  const executeMigration = async () => {
    if (!migrationPreview || migrationPreview.length === 0) return;

    setIsMigrating(true);
    const batchSize = 450; // Firestore limit is 500, safe margin
    const chunks = [];

    for (let i = 0; i < migrationPreview.length; i += batchSize) {
      chunks.push(migrationPreview.slice(i, i + batchSize));
    }

    try {
      let updatedCount = 0;

      for (const chunk of chunks) {
        const batch = database.batch();

        chunk.forEach((item) => {
          const docRef = database.collection("presenze").doc(item.presenzaId);
          batch.update(docRef, { cantiere: item.newCantiere });
        });

        await batch.commit();
        updatedCount += chunk.length;
        console.log(
          `Migrati ${updatedCount}/${migrationPreview.length} documenti`,
        );
      }

      alert(
        `Migrazione completata con successo! ${updatedCount} presenze aggiornate.`,
      );
      setShowMigrationModal(false);
      setMigrationPreview(null);
      fetchAllData(); // Ricarica i dati
    } catch (error) {
      console.error("Errore durante la migrazione:", error);
      alert(
        "Si è verificato un errore durante la migrazione. Controlla la console.",
      );
    } finally {
      setIsMigrating(false);
    }
  };

  // Funzione per generare un'anteprima dei dati
  const generaAnteprimaDati = () => {
    console.log("Generazione anteprima - Utente selezionato ID:", selectedUser);
    console.log("Utenti disponibili:", users.length);

    const utenteTrovato = users.find((u) => u.id === selectedUser);
    console.log(
      "Utente trovato:",
      utenteTrovato ? utenteTrovato.id : "non trovato",
    );

    if (utenteTrovato) {
      console.log("Dettagli utente trovato:", {
        id: utenteTrovato.id,
        displayName: utenteTrovato.displayName,
        email: utenteTrovato.email,
        anagrafica: utenteTrovato.anagrafica,
      });
    }

    const nomeUtente = utenteTrovato
      ? utenteTrovato.anagrafica
        ? `${utenteTrovato.anagrafica.nome} ${utenteTrovato.anagrafica.cognome}`
        : utenteTrovato.displayName
      : "Utente non trovato";
    const nomeMese = [
      "Gennaio",
      "Febbraio",
      "Marzo",
      "Aprile",
      "Maggio",
      "Giugno",
      "Luglio",
      "Agosto",
      "Settembre",
      "Ottobre",
      "Novembre",
      "Dicembre",
    ][meseSelezionato];

    // Ottieni le presenze ordinate
    const presenzeOrdinate = ordinaPresenze(presenzeUtente);

    return {
      utente: nomeUtente,
      periodo: `${nomeMese} ${annoSelezionato}`,
      numeroPresenze: presenzeOrdinate.length,
      tuttiGiorni: presenzeOrdinate.map((presenza) => {
        const data = presenza.dataRegistrazione.toDate();
        const giorno = format(data, "dd - eeee", { locale: it });
        const oraIngresso =
          presenza.oraIngresso &&
          typeof presenza.oraIngresso.toDate === "function"
            ? format(presenza.oraIngresso.toDate(), "HH:mm")
            : "-";
        const oraUscita =
          presenza.oraUscita && typeof presenza.oraUscita.toDate === "function"
            ? format(presenza.oraUscita.toDate(), "HH:mm")
            : "-";
        let cantiere = "-";
        if (presenza.isFerie) {
          cantiere = "Ferie";
        } else if (presenza.isMalattia) {
          cantiere = `Malattia ${presenza.codiceMalattia || ""}`;
        } else if (presenza.isRiposo) {
          cantiere = presenza.note || "Riposo";
        } else {
          cantiere = presenza.cantiere?.nome || "-";
        }

        return { giorno, oraIngresso, oraUscita, cantiere };
      }),
    };
  };

  return (
    <div className="presenze-container">
      <h2
        style={{
          marginBottom: "30px",
          borderBottom: "2px solid #007bff",
          paddingBottom: "10px",
          display: "inline-block",
        }}
      >
        Pannello Amministrazione Presenze
      </h2>

      {isLoading ? (
        <div className="loading-container">
          <p>Caricamento dati in corso...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p>Errore: {error}</p>
        </div>
      ) : (
        <>
          <div className="admin-dashboard-grid">
            {/* CARD 1: Configurazione e Filtri */}
            <div className="admin-card">
              <div className="admin-card-header">
                <h3>🔍 Filtri e Selezione</h3>
              </div>
              <div className="admin-card-content">
                <div className="admin-section-title">
                  Periodo di Riferimento
                </div>
                <div style={{ display: "flex", gap: "15px" }}>
                  <div style={{ flex: 1 }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "5px",
                        fontSize: "0.9rem",
                      }}
                    >
                      Mese
                    </label>
                    <Select
                      id="mese"
                      value={monthOptions.find(
                        (option) => option.value === meseSelezionato,
                      )}
                      onChange={handleChangeMese}
                      options={monthOptions}
                      styles={customSelectStyles}
                      className="react-select-container"
                      classNamePrefix="react-select"
                      menuPortalTarget={document.body}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "5px",
                        fontSize: "0.9rem",
                      }}
                    >
                      Anno
                    </label>
                    <Select
                      id="anno"
                      value={yearOptions.find(
                        (option) => option.value === annoSelezionato,
                      )}
                      onChange={handleChangeAnno}
                      options={yearOptions}
                      styles={customSelectStyles}
                      className="react-select-container"
                      classNamePrefix="react-select"
                      menuPortalTarget={document.body}
                    />
                  </div>
                </div>

                <div
                  className="admin-section-title"
                  style={{ marginTop: "10px" }}
                >
                  Dipendente
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontSize: "0.9rem",
                    }}
                  >
                    Seleziona Utente
                  </label>
                  <Select
                    id="user-select"
                    value={userOptions.find(
                      (option) => option.value === selectedUser,
                    )}
                    onChange={handleChangeUser}
                    options={userOptions}
                    styles={customSelectStyles}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    placeholder="Cerca dipendente..."
                  />
                </div>
                <div style={{ marginTop: "10px" }}>
                  <label style={{ display: "flex", alignItems: "center" }}>
                    <input
                      type="checkbox"
                      checked={includeCessati}
                      onChange={(e) => setIncludeCessati(e.target.checked)}
                      style={{ marginRight: "8px" }}
                    />
                    Includi cessati
                  </label>
                </div>

                <button
                  className="btn-mostra-anteprima"
                  onClick={() => setMostraAnteprima(true)}
                  style={{ marginTop: "10px" }}
                >
                  Mostra Anteprima Presenze
                </button>
                <button
                  className="btn-mostra-anteprima"
                  onClick={() => setShowValorizationPreview(true)}
                  style={{ marginTop: "10px", backgroundColor: "#17a2b8" }}
                >
                  Anteprima Valorizzazione
                </button>
              </div>
            </div>

            {/* CARD: Gestione Bonus (Visibile solo se utente selezionato) */}
            {selectedUser && (
              <BonusManager
                user={users.find((u) => u.id === selectedUser)}
                month={meseSelezionato}
                year={annoSelezionato}
              />
            )}

            {/* CARD: Inserimento Periodico (Nuova Card) */}
            <div className="admin-card">
              <div className="admin-card-header">
                <h3>📅 Inserimento Presenze Periodiche</h3>
              </div>
              <div className="admin-card-content">
                <p
                  style={{
                    fontSize: "0.9rem",
                    color: "#666",
                    marginBottom: "15px",
                  }}
                >
                  Inserisci presenze per un intervallo di date per l'utente
                  selezionato:
                  <strong>
                    {" "}
                    {users.find((u) => u.id === selectedUser)?.displayName ||
                      "Nessun utente selezionato"}
                  </strong>
                </p>

                <div
                  style={{ display: "flex", gap: "15px", marginBottom: "15px" }}
                >
                  <div style={{ flex: 1 }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "5px",
                        fontSize: "0.9rem",
                      }}
                    >
                      Data Inizio
                    </label>
                    <input
                      type="date"
                      value={periodicStartDate}
                      onChange={(e) => setPeriodicStartDate(e.target.value)}
                      className="form-control"
                      style={{
                        width: "100%",
                        padding: "8px",
                        borderRadius: "4px",
                        border: "1px solid #ddd",
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "5px",
                        fontSize: "0.9rem",
                      }}
                    >
                      Data Fine
                    </label>
                    <input
                      type="date"
                      value={periodicEndDate}
                      onChange={(e) => setPeriodicEndDate(e.target.value)}
                      className="form-control"
                      style={{
                        width: "100%",
                        padding: "8px",
                        borderRadius: "4px",
                        border: "1px solid #ddd",
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: "15px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontSize: "0.9rem",
                    }}
                  >
                    Tipo Presenza
                  </label>
                  <select
                    value={periodicType}
                    onChange={(e) => setPeriodicType(e.target.value)}
                    className="form-control"
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "4px",
                      border: "1px solid #ddd",
                    }}
                  >
                    <option value="lavorativa">Lavorativa</option>
                    <option value="ferie">Ferie</option>
                    <option value="malattia">Malattia</option>
                    <option value="riposo">Riposo</option>
                  </select>
                </div>

                {periodicType === "lavorativa" && (
                  <>
                    <div
                      style={{
                        display: "flex",
                        gap: "15px",
                        marginBottom: "15px",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "5px",
                            fontSize: "0.9rem",
                          }}
                        >
                          Ora Ingresso
                        </label>
                        <input
                          type="time"
                          value={periodicIngresso}
                          onChange={(e) => setPeriodicIngresso(e.target.value)}
                          className="form-control"
                          style={{
                            width: "100%",
                            padding: "8px",
                            borderRadius: "4px",
                            border: "1px solid #ddd",
                          }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "5px",
                            fontSize: "0.9rem",
                          }}
                        >
                          Ora Uscita
                        </label>
                        <input
                          type="time"
                          value={periodicUscita}
                          onChange={(e) => setPeriodicUscita(e.target.value)}
                          className="form-control"
                          style={{
                            width: "100%",
                            padding: "8px",
                            borderRadius: "4px",
                            border: "1px solid #ddd",
                          }}
                        />
                      </div>
                    </div>
                    <div style={{ marginBottom: "15px" }}>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "5px",
                          fontSize: "0.9rem",
                        }}
                      >
                        Cantiere
                      </label>
                      <Select
                        value={
                          periodicCantiere
                            ? {
                                label: periodicCantiere.nome,
                                value: periodicCantiere,
                              }
                            : null
                        }
                        onChange={(option) =>
                          setPeriodicCantiere(
                            option
                              ? {
                                  nome: option.label,
                                  codice: option.value.codice,
                                }
                              : null,
                          )
                        }
                        options={projects.map((p) => ({
                          label: p.nome,
                          value: p,
                        }))}
                        styles={customSelectStyles}
                        placeholder="Seleziona Cantiere..."
                        isClearable
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "15px",
                        marginBottom: "15px",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "5px",
                            fontSize: "0.9rem",
                          }}
                        >
                          Mezzo
                        </label>
                        <Select
                          value={
                            periodicMezzo
                              ? {
                                  label: `${periodicMezzo.modello} - ${periodicMezzo.targa}`,
                                  value: periodicMezzo,
                                }
                              : null
                          }
                          onChange={(option) =>
                            setPeriodicMezzo(option ? option.value : null)
                          }
                          options={automezzi.map((m) => ({
                            label: `${m.modello} - ${m.targa}`,
                            value: m,
                          }))}
                          styles={customSelectStyles}
                          placeholder="Seleziona Mezzo..."
                          isClearable
                        />
                      </div>
                      <div style={{ width: "120px" }}>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "5px",
                            fontSize: "0.9rem",
                          }}
                        >
                          Pausa (ore)
                        </label>
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          value={periodicPausa}
                          onChange={(e) => setPeriodicPausa(e.target.value)}
                          className="form-control"
                          style={{
                            width: "100%",
                            padding: "8px",
                            borderRadius: "4px",
                            border: "1px solid #ddd",
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}

                {periodicType === "malattia" && (
                  <div style={{ marginBottom: "15px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "5px",
                        fontSize: "0.9rem",
                      }}
                    >
                      Codice Malattia (Opzionale)
                    </label>
                    <input
                      type="text"
                      value={periodicCodiceMalattia}
                      onChange={(e) =>
                        setPeriodicCodiceMalattia(e.target.value)
                      }
                      className="form-control"
                      style={{
                        width: "100%",
                        padding: "8px",
                        borderRadius: "4px",
                        border: "1px solid #ddd",
                      }}
                    />
                  </div>
                )}

                <div style={{ marginBottom: "15px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontSize: "0.9rem",
                    }}
                  >
                    Note
                  </label>
                  <input
                    type="text"
                    value={periodicNote}
                    onChange={(e) => setPeriodicNote(e.target.value)}
                    className="form-control"
                    placeholder="Note opzionali..."
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "4px",
                      border: "1px solid #ddd",
                    }}
                  />
                </div>

                <button
                  onClick={handleBulkPeriodicCreation}
                  disabled={isSubmittingPeriodic || !selectedUser}
                  style={{
                    padding: "10px 15px",
                    backgroundColor: "#007bff",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor:
                      isSubmittingPeriodic || !selectedUser
                        ? "not-allowed"
                        : "pointer",
                    width: "100%",
                    fontWeight: "bold",
                  }}
                >
                  {isSubmittingPeriodic
                    ? "Inserimento in corso..."
                    : "Inserisci Presenze Periodiche"}
                </button>
              </div>
            </div>

            {/* CARD 2: Strumenti Amministrativi */}
            <div className="admin-card">
              <div className="admin-card-header">
                <h3>🛠️ Strumenti Rapidi</h3>
              </div>
              <div className="admin-card-content">
                {/* Inserimento Massivo */}
                <div
                  style={{
                    backgroundColor: "#f8f9fa",
                    padding: "15px",
                    borderRadius: "8px",
                    border: "1px solid #eee",
                  }}
                >
                  <div
                    className="admin-section-title"
                    style={{ color: "#28a745" }}
                  >
                    Inserimento Festività
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "5px",
                          fontSize: "0.85rem",
                        }}
                      >
                        Data Festa
                      </label>
                      <input
                        type="date"
                        value={festaDate}
                        onChange={(e) => setFestaDate(e.target.value)}
                        className="form-control"
                        style={{
                          width: "100%",
                          padding: "8px",
                          borderRadius: "4px",
                          border: "1px solid #ddd",
                        }}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "5px",
                          fontSize: "0.85rem",
                        }}
                      >
                        Nota (default: "Festa")
                      </label>
                      <input
                        type="text"
                        value={festaNota}
                        onChange={(e) => setFestaNota(e.target.value)}
                        className="form-control"
                        placeholder="Inserisci nota (es. Santo Patrono)"
                        style={{
                          width: "100%",
                          padding: "8px",
                          borderRadius: "4px",
                          border: "1px solid #ddd",
                        }}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "5px",
                          fontSize: "0.85rem",
                        }}
                      >
                        Dipendenti (Multi-selezione)
                      </label>
                      <div className="checkbox-list-container">
                        <div className="checkbox-list-header">
                          <label>
                            <input
                              type="checkbox"
                              checked={
                                userOptions.length > 0 &&
                                selectedUsersForFesta.length ===
                                  userOptions.length
                              }
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedUsersForFesta(userOptions);
                                } else {
                                  setSelectedUsersForFesta([]);
                                }
                              }}
                            />
                            Seleziona Tutti ({userOptions.length})
                          </label>
                        </div>
                        <div className="checkbox-list-body">
                          {userOptions.map((option) => (
                            <div
                              key={option.value}
                              className="checkbox-list-item"
                            >
                              <label>
                                <input
                                  type="checkbox"
                                  checked={selectedUsersForFesta.some(
                                    (u) => u.value === option.value,
                                  )}
                                  onChange={() => {
                                    setSelectedUsersForFesta((prev) => {
                                      const exists = prev.some(
                                        (u) => u.value === option.value,
                                      );
                                      if (exists) {
                                        return prev.filter(
                                          (u) => u.value !== option.value,
                                        );
                                      } else {
                                        return [...prev, option];
                                      }
                                    });
                                  }}
                                />
                                {option.label}
                              </label>
                            </div>
                          ))}
                        </div>
                        <div className="checkbox-list-footer">
                          Selezionati:{" "}
                          <strong>{selectedUsersForFesta.length}</strong>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={handleBulkFestaCreation}
                      disabled={isSubmittingFesta}
                      style={{
                        padding: "8px 15px",
                        backgroundColor: "#28a745",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: isSubmittingFesta ? "not-allowed" : "pointer",
                        width: "100%",
                        fontWeight: "bold",
                      }}
                    >
                      {isSubmittingFesta
                        ? "Inserimento..."
                        : "Inserisci Festa a Selezionati"}
                    </button>
                  </div>
                </div>

                {/* Manutenzione Dati */}
                <div
                  style={{
                    marginTop: "20px",
                    borderTop: "1px solid #eee",
                    paddingTop: "15px",
                  }}
                >
                  <div className="admin-section-title">Manutenzione Dati</div>
                  <p
                    style={{
                      fontSize: "0.85rem",
                      color: "#666",
                      marginBottom: "10px",
                    }}
                  >
                    Strumenti per la correzione e normalizzazione dei dati
                    storici.
                  </p>
                  <button
                    className="btn-mostra-anteprima"
                    style={{
                      width: "100%",
                      backgroundColor: "#6c757d",
                      marginTop: "5px",
                    }}
                    onClick={analyzePresenzeForMigration}
                    disabled={isLoading || tuttePresenze.length === 0}
                  >
                    🔄 Aggiungi Codici Cantiere (Migrazione)
                  </button>
                </div>

                {/* Export Globali */}
                <div style={{ marginTop: "auto" }}>
                  <div className="admin-section-title">
                    Reportistica Mensile (Tutti)
                  </div>
                  <div
                    style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}
                  >
                    {/* Pulsanti Export */}
                    {allUsersData.length > 0 &&
                      allUsersData.some(
                        (userData) => userData.presenze.length > 0,
                      ) && (
                        <>
                          <button
                            className="btn-download-pdf btn-download-all"
                            style={{
                              flex: 1,
                              margin: 0,
                              fontSize: "0.85rem",
                              padding: "10px",
                            }}
                            onClick={async () => {
                              try {
                                setGeneratingMultiUserPdf(true);
                                const periodo = {
                                  mese: meseSelezionato,
                                  anno: annoSelezionato,
                                };
                                const baseUsers = allUsersData.filter(
                                  (userData) => userData.presenze.length > 0,
                                );
                                const usersData = await Promise.all(
                                  baseUsers.map(async (userData) => {
                                    const bonuses =
                                      await fetchConfirmedBonusesForUser(
                                        userData?.user?.id,
                                        annoSelezionato,
                                        meseSelezionato,
                                      );
                                    return {
                                      ...userData,
                                      presenze: ordinaPresenze(
                                        userData.presenze,
                                      ),
                                      confirmedBonuses: bonuses,
                                    };
                                  }),
                                );
                                const label = format(
                                  new Date(annoSelezionato, meseSelezionato),
                                  "MMMM_yyyy",
                                  { locale: it },
                                );
                                const fileName = `presenze_tutti_dipendenti_${label}.pdf`;
                                await downloadPresenzeMultiPdf({
                                  usersData,
                                  periodo,
                                  fileName,
                                  projects,
                                  documenti: tuttiDocumenti,
                                  isAdmin: admin,
                                });
                              } catch (e) {
                                console.error("Errore PDF multi:", e);
                              } finally {
                                setGeneratingMultiUserPdf(false);
                              }
                            }}
                          >
                            {generatingMultiUserPdf
                              ? "Generazione..."
                              : "📥 PDF Completo"}
                          </button>
                          <button
                            className="btn-download-pdf"
                            style={{
                              flex: 1,
                              margin: 0,
                              backgroundColor: "#17a2b8",
                              fontSize: "0.85rem",
                              padding: "10px",
                            }}
                            onClick={handleExportCsv}
                          >
                            📊 CSV Excel
                          </button>
                        </>
                      )}
                  </div>
                </div>
              </div>
            </div>

            {/* CARD 3: Gestione Permessi */}
            <div className="admin-card">
              <div className="admin-card-header">
                <h3>🔐 Gestione Permessi</h3>
              </div>
              <div className="admin-card-content">
                <div className="admin-section-title">
                  Accesso Valorizzazione
                </div>
                <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th
                          style={{
                            textAlign: "left",
                            padding: "8px",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          Dipendente
                        </th>
                        <th
                          style={{
                            textAlign: "center",
                            padding: "8px",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          Abilitato
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleUsers.map((user) => (
                        <tr
                          key={user.id}
                          style={{ borderBottom: "1px solid #f9f9f9" }}
                        >
                          <td style={{ padding: "8px" }}>
                            {user.anagrafica
                              ? `${user.anagrafica.nome} ${user.anagrafica.cognome}`
                              : user.displayName || user.email}
                          </td>
                          <td style={{ padding: "8px", textAlign: "center" }}>
                            <input
                              type="checkbox"
                              checked={user.canViewValorization || false}
                              onChange={async (e) => {
                                const newValue = e.target.checked;
                                try {
                                  await database
                                    .collection("users")
                                    .doc(user.id)
                                    .update({
                                      canViewValorization: newValue,
                                    });
                                  setUsers((prev) =>
                                    prev.map((u) =>
                                      u.id === user.id
                                        ? {
                                            ...u,
                                            canViewValorization: newValue,
                                          }
                                        : u,
                                    ),
                                  );
                                } catch (error) {
                                  console.error(
                                    "Errore aggiornamento permesso:",
                                    error,
                                  );
                                  alert("Errore durante l'aggiornamento.");
                                }
                              }}
                              style={{
                                width: "18px",
                                height: "18px",
                                cursor: "pointer",
                              }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Anteprima dati */}
          {mostraAnteprima && (
            <div className="anteprima-dati" style={{ marginTop: "0" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                  borderBottom: "1px solid #eee",
                  paddingBottom: "10px",
                }}
              >
                <h3 style={{ margin: 0 }}>📋 Dettaglio Presenze</h3>
                {presenzeUtente && presenzeUtente.length > 0 && (
                  <button
                    className="btn-download-pdf"
                    style={{ margin: 0 }}
                    onClick={async () => {
                      try {
                        const userObj = users.find(
                          (u) => u.id === selectedUser,
                        ) || {
                          displayName: "Utente non trovato",
                          email: "",
                          anagrafica: null,
                        };
                        const periodo = {
                          mese: meseSelezionato,
                          anno: annoSelezionato,
                        };
                        const label = format(
                          new Date(annoSelezionato, meseSelezionato),
                          "MMMM_yyyy",
                          { locale: it },
                        );
                        const safeUser = (
                          userObj?.displayName ||
                          userObj?.email ||
                          "utente"
                        )
                          .replace(/[^a-zA-Z0-9_-]/g, "_")
                          .substring(0, 50);
                        const fileName = `presenze_${safeUser}_${label}.pdf`;
                        const bonuses = await fetchConfirmedBonusesForUser(
                          selectedUser,
                          annoSelezionato,
                          meseSelezionato,
                        );
                        downloadPresenzePdf({
                          presenze: ordinaPresenze(presenzeUtente),
                          user: userObj,
                          periodo,
                          fileName,
                          reperibilita: reperibilitaUtente,
                          projects,
                          documenti: tuttiDocumenti,
                          isAdmin: admin,
                          confirmedBonuses: bonuses,
                        });
                      } catch (e) {
                        console.error("Errore PDF singolo:", e);
                      }
                    }}
                  >
                    📥 Scarica PDF Dipendente
                  </button>
                )}
              </div>

              {(() => {
                const anteprima = generaAnteprimaDati();
                return (
                  <div className="anteprima-contenuto">
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(200px, 1fr))",
                        gap: "20px",
                        marginBottom: "20px",
                        backgroundColor: "#fff",
                        padding: "15px",
                        borderRadius: "8px",
                        border: "1px solid #eee",
                      }}
                    >
                      <div>
                        <strong>Dipendente:</strong> <br /> {anteprima.utente}
                      </div>
                      <div>
                        <strong>Periodo:</strong> <br /> {anteprima.periodo}
                      </div>
                      <div>
                        <strong>Totale Presenze:</strong> <br />{" "}
                        {anteprima.numeroPresenze}
                      </div>
                    </div>

                    {anteprima.numeroPresenze > 0 ? (
                      <div className="anteprima-tabella-container">
                        <table
                          className="anteprima-tabella"
                          style={{ width: "100%", borderCollapse: "collapse" }}
                        >
                          <thead>
                            <tr
                              style={{
                                backgroundColor: "#f1f3f5",
                                textAlign: "left",
                              }}
                            >
                              <th
                                style={{
                                  padding: "12px",
                                  borderBottom: "2px solid #dee2e6",
                                }}
                              >
                                Giorno
                              </th>
                              <th
                                style={{
                                  padding: "12px",
                                  borderBottom: "2px solid #dee2e6",
                                }}
                              >
                                Ingresso
                              </th>
                              <th
                                style={{
                                  padding: "12px",
                                  borderBottom: "2px solid #dee2e6",
                                }}
                              >
                                Uscita
                              </th>
                              <th
                                style={{
                                  padding: "12px",
                                  borderBottom: "2px solid #dee2e6",
                                }}
                              >
                                Cantiere
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {anteprima.tuttiGiorni.map((giorno, index) => (
                              <tr
                                key={index}
                                style={{ borderBottom: "1px solid #eee" }}
                              >
                                <td style={{ padding: "10px" }}>
                                  {giorno.giorno}
                                </td>
                                <td style={{ padding: "10px" }}>
                                  {giorno.oraIngresso}
                                </td>
                                <td style={{ padding: "10px" }}>
                                  {giorno.oraUscita}
                                </td>
                                <td style={{ padding: "10px" }}>
                                  {giorno.cantiere &&
                                  typeof giorno.cantiere === "object"
                                    ? giorno.cantiere.nome
                                    : giorno.cantiere}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "40px",
                          color: "#6c757d",
                          backgroundColor: "white",
                          borderRadius: "8px",
                        }}
                      >
                        <p>Nessuna presenza registrata per questo periodo.</p>
                      </div>
                    )}
                  </div>
                );
              })()}

              <div style={{ textAlign: "center", marginTop: "20px" }}>
                <button
                  className="btn-chiudi-anteprima"
                  onClick={() => setMostraAnteprima(false)}
                >
                  Chiudi anteprima
                </button>
              </div>
            </div>
          )}
        </>
      )}
      {showValorizationPreview && (
        <ValorizationModal
          show={showValorizationPreview}
          onClose={() => setShowValorizationPreview(false)}
          presenze={presenzeUtente}
          reperibilita={reperibilitaUtente}
          documenti={tuttiDocumenti}
          spese={tutteSpese}
          user={users.find((u) => u.id === selectedUser)}
          monthFilter={format(
            new Date(annoSelezionato, meseSelezionato),
            "yyyy-MM",
            { locale: it },
          )}
        />
      )}

      {/* Modale Anteprima Migrazione */}
      {showMigrationModal && migrationPreview && (
        <div className="presenze-modal-overlay">
          <div
            className="presenze-modal-content"
            style={{ maxWidth: "900px", maxHeight: "90vh", overflowY: "auto" }}
          >
            <h3>Anteprima Migrazione Codici Cantiere</h3>
            <p>
              Verranno aggiornati <strong>{migrationPreview.length}</strong>{" "}
              documenti. Controlla i dati prima di confermare.
            </p>

            <div
              style={{
                maxHeight: "60vh",
                overflowY: "auto",
                border: "1px solid #ddd",
                margin: "15px 0",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "0.9rem",
                }}
              >
                <thead>
                  <tr
                    style={{
                      position: "sticky",
                      top: 0,
                      background: "#f8f9fa",
                    }}
                  >
                    <th style={{ padding: "8px", textAlign: "left" }}>Data</th>
                    <th style={{ padding: "8px", textAlign: "left" }}>
                      Utente
                    </th>
                    <th style={{ padding: "8px", textAlign: "left" }}>
                      Vecchio Valore
                    </th>
                    <th style={{ padding: "8px", textAlign: "left" }}>
                      Nuovo Valore (Codice)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {migrationPreview.slice(0, 100).map((item, index) => (
                    <tr key={index} style={{ borderBottom: "1px solid #eee" }}>
                      <td style={{ padding: "8px" }}>
                        {format(item.dataRegistrazione.toDate(), "dd/MM/yyyy", {
                          locale: it,
                        })}
                      </td>
                      <td style={{ padding: "8px" }}>{item.user}</td>
                      <td style={{ padding: "8px", color: "#dc3545" }}>
                        {typeof item.oldCantiere === "object"
                          ? `${item.oldCantiere.nome} (${item.oldCantiere.codice || "No Codice"})`
                          : item.oldCantiere}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          color: "#28a745",
                          fontWeight: "bold",
                        }}
                      >
                        {item.newCantiere.nome} ({item.newCantiere.codice})
                      </td>
                    </tr>
                  ))}
                  {migrationPreview.length > 100 && (
                    <tr>
                      <td
                        colSpan="4"
                        style={{
                          padding: "10px",
                          textAlign: "center",
                          fontStyle: "italic",
                        }}
                      >
                        ... e altri {migrationPreview.length - 100} documenti
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div
              className="modal-actions"
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <button
                className="btn-cancel"
                onClick={() => {
                  setShowMigrationModal(false);
                  setMigrationPreview(null);
                }}
                disabled={isMigrating}
              >
                Annulla
              </button>
              <button
                className="btn-confirm"
                onClick={executeMigration}
                disabled={isMigrating}
                style={{ backgroundColor: "#28a745", color: "white" }}
              >
                {isMigrating
                  ? "Migrazione in corso..."
                  : "Conferma e Aggiorna Database"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
